<!--
  Displays all the items of the skill
-->

<template>
  <div v-loading="state.loading">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>
        <router-link :to="{ name: 'home', query: {view: view, compare: compare} }">Home</router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item>Skill summary</el-breadcrumb-item>
    </el-breadcrumb>
    <main-title>Skill: {{value.name}}</main-title>
    <!-- a popup to edit some skill properties -->
    <skill-editor
      :value="value"
      :visible.sync="editing"
      @input="save"></skill-editor>
    <view-selector
      :view="view"
      :compare="compare"
      @update="changeView"
      :object="value"
      :publishable="true"
      :edit="true"
      name="skill"
      @save="edit"
      @delete="remove"
      @test="test"
      @untest="untest"
      @publish="publish"
      @unpublish="unpublish"
      @unprod="unprod"
      @reject="reject"
      @approve="approve"
      @disapprove="disapprove"
      @restore="restore"
      ></view-selector>
    <!-- the filter bar -->
    <div id="skill-page-filter">
      <el-input
        v-model="filter"
        placeholder="filter …"
        clearable>
      </el-input>
    </div>
    <!-- the intents, by context -->
    <div v-for="context in filteredIntents" :key="context.context">
      <!-- the context name and link, depending on the type of context -->
      <subtitle v-if="!context.context"
        id="global-context">
        <template slot="tooltip">
          The <em>intents</em> that can be called when starting the <em>skill</em>.
        </template>Global context</subtitle>
      <subtitle v-else-if="context.context === '#'"
        id="special-intents">
        <template slot="tooltip">
          <em>Special intents</em> for specific actions.
        </template>Special intents</subtitle>
      <subtitle v-else
        :id="'context-' + context.context">
        <template slot="tooltip">
          These <em>intents</em> can only be called when the <em>context </em> <strong>{{ context.context }}</strong> is active.
        </template>Context: {{context.context}}</subtitle>
      <!-- the intents of the context -->
      <el-tree class="intent-list"
        ref="intentsTrees"
        :data="context.intents"
        node-key="intent"
        :default-expand-all="!!delayedFilter"
        :default-expanded-keys="expanded.intents"
        v-if="context.intents.length">
        <template v-slot="{ data: intent }">
          <div class="intent-list-item">
            <!-- a node, to group intents together -->
            <span v-if="intent.type==='node'"
              class="intent-list-main">
              <span class="intent-list-count"
                >&nbsp;&gt;&nbsp;</span>
              <span class="intent-list-node intent-list-name">
                {{intent.shortName}}
              </span>
            </span>
            <!-- an intent -->
            <template v-else>
              <span class="intent-list-main"
                v-id="'intents-' + intent.intent">
                <!-- number of inputs -->
                <span class="intent-list-count"
                  >({{ intent.first.inputs_count || intent.first.inputsCount || '-' }})</span>
                <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
                  <i :class="intent.first.status==='deleted'?'el-icon-circle-close':intent.first.status==='created'?'el-icon-circle-plus-outline':intent.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
                  <template slot="content">
                    {{intent.first.explanation}}
                  </template>
                </el-tooltip>
                <!-- the followup-context prefix -->
                <el-tooltip v-if="intent.first.followup"
                  placement="top" effect="light" :enterable="false" :tabindex="-1">
                  <template slot="content">
                    This intent is a <em>followup</em> of <strong>"{{intent.parent.name}}"</strong>.<br/>
                    It will be available after its parent is executed.
                  </template>
                  <span class="intent-list-followup">⮡</span>
                </el-tooltip>
                <!-- name of the view intent (or compare if not in view) -->
                <router-link
                  :to="{name: 'intent', params: {skill: skill, intent: intent.intent}, query: intent.query}"
                  class="intent-list-intent intent-list-name"
                  :class="'intent-list-' + intent.first.source"
                  >{{intent.shortName}}</router-link>
                <!-- context of the compare intent if different from view -->
                <template v-if="intent.second && intent.second.context !== intent.first.context">
                  <span v-if="!intent.second.followup"
                    class="intent-list-context intent-list-old"
                    >[{{intent.second.context || 'Global context'}}]
                  </span>
                  <router-link v-else
                    class="intent-list-context intent-list-context-followup intent-list-old"
                    :to="{name: 'intent', params: {skill: skill, intent: intent.second.followup}, query: allIntents[intent.second.followup].query}"
                    >[⮡{{
                      shortenName(intent.prefix, allIntents[intent.second.followup].name)
                    }}]
                  </router-link>
                </template>
                <!-- name of the compare intent if different from view -->
                <router-link v-if="intent.second"
                  :to="{name: 'intent', params: {skill: skill, intent: intent.intent}, query: intent.query}"
                  class="intent-list-intent intent-list-name"
                  :class="'intent-list-' + intent.second.source">{{
                    shortenName(intent.prefix, intent.second.name)
                  }}</router-link>
                <!-- a link to create followup-context intents -->
                <el-tooltip v-if="!view && context.context !== '#' && intent.first.timestamp"
                  placement="top" effect="light" :enterable="false" :tabindex="-1">
                  <router-link
                    :to="{name: 'intent', params: {skill: skill, intent: 'new'}, query: {context: 'followup:' + intent.intent}}"
                    class="intent-list-add-followup"
                  >⭢</router-link>
                  <template slot="content">
                    Add <em>followup intent</em> to <strong>"{{intent.first.name}}"</strong>
                  </template>
                </el-tooltip>
              </span>
              <span class="intent-list-infos">
                <!-- all the output contexts -->
                <span class="intent-list-outputs"
                  v-if="intent.outputs.length">
                  (<span v-for="(output, index) in intent.outputs"
                    :key="index">{{index ? ', ' : ''
                    }}<el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
                      <span v-if="!output.followup"
                      class="intent-list-output"
                      :class="[
                        output.exclusive ? 'intent-list-output-exclusive' :
                        output.ttl === 0 ? 'intent-list-output-delete' :
                        output.extend ? 'intent-list-output-extend' :
                        output.ttl === -1 ? 'intent-list-output-infinite' :
                        'intent-list-output-finite',
                        'intent-list-' + output.source]"
                        >{{output.context
                      }}</span><router-link v-else
                        :to="{name: 'intent', params: {skill: skill, intent: output.followup}, query: allIntents[output.followup].query}"
                        class="intent-list-output intent-list-output-followup"
                        :key="index" :class="[
                          output.exclusive ? 'intent-list-output-exclusive' :
                          output.ttl === 0 ? 'intent-list-output-delete' :
                          output.extend ? 'intent-list-output-extend' :
                          output.ttl === -1 ? 'intent-list-output-infinite' :
                          'intent-list-output-finite',
                          'intent-list-' + output.source]"
                        >⮡<span
                          v-if="output.followup !== intent.intent">{{
                            shortenName(intent.prefix, allIntents[output.followup].name)
                        }}</span></router-link>
                      <template slot="content">
                        <template v-if="!output.followup"
                          >Context <strong>"{{output.context}}"</strong></template>
                        <template v-else-if="output.followup !== intent.intent"
                          >Followup context of <strong>"{{
                            shortenName(intent.prefix, allIntents[output.followup].name)
                          }}"</strong></template>
                        <template v-else
                          >Followup context</template>
                        <br/>
                        <template v-if="output.exclusive">
                          <em>Exclusive</em> (temporarily disables other contexts)
                        </template>
                        <template v-else-if="output.ttl === 0">
                          <em>Deleted</em>
                        </template>
                        <template v-else-if="output.ttl === -1">
                          <em>Unlimited</em> (does not expires unless deleted)
                        </template>
                        <template v-else>
                          <em>Expires</em> after <strong>{{output.ttl}}</strong> turns
                        </template>
                        <template v-if="output.extend">
                          (<em>refreshed</em>)
                        </template>
                      </template>
                    </el-tooltip></span>)
                </span>
                <!-- all follow intents -->
                <span class="intent-list-outputs"
                  v-if="intent.follow.length"> :
                  <template
                    v-for="(follow, index) in intent.follow.filter(follow => follow.intent)"
                    >{{ index ? ', ' : ''
                  }}<router-link v-if="follow.skill === skill && allIntents[follow.intent]"
                      :key="index"
                      :to="{name: 'intent', params: {skill: skill, intent: follow.intent}, query: allIntents[follow.intent].query}"
                      class="intent-list-intent intent-list-follow"
                      :class="'intent-list-' + follow.source">{{
                        shortenName(intent.prefix, allIntents[follow.intent].name)
                      }}</router-link>
                    <span v-else-if="follow.skill !== skill && (allPublic[follow.skill] || {})[follow.intent]"
                      :key="index"
                      :class="'intent-list-' + follow.source"
                      >[{{ allPublic[follow.skill][follow.intent].skill
                    }}] {{allPublic[follow.skill][follow.intent].intent}}</span>
                    <el-tooltip v-else :key="index"
                      placement="top" effect="light" :enterable="false" :tabindex="-1">
                      <i :key="index" class="el-icon-error"></i>
                      <template slot="content">
                        <em>followup intent</em> not found
                        <strong v-if="follow.skill === skill">{{follow.intent}}</strong>
                        <strong v-else>{{follow.skill}}/{{follow.intent}}</strong>
                      </template>
                    </el-tooltip>
                  </template>
                </span>
              </span>
            </template>
          </div>
        </template>
      </el-tree>
      <!-- link for a new context -->
      <div class="intent-list" v-if="!view && context.context !== '#'">
        <div class="intent-list-item" style="margin-top: 0.5em">
          <span class="intent-list-main">
            <span class="intent-list-count"
              >&nbsp;+</span>
            <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
              <router-link
                :to="{name: 'intent', params: {skill: skill, intent: 'new'}, query: {context: context.context || undefined}}"
                class="intent-list-intent intent-list-add"
                >New intent</router-link>
              <template slot="content">
                Add an <em>intent</em> in the
                <template v-if="context.context">
                  <em>context </em> <strong>"{{context.context}}"</strong>
                </template>
                <template v-else>
                  <strong>global</strong> <em> context</em>
                </template>
              </template>
            </el-tooltip>
          </span>
        </div>
      </div>
    </div>
    <!-- the entities -->
    <div v-if="!delayedFilter || filteredEntities.length">
      <subtitle id="entities">
        <template slot="tooltip">
          <em>Entities</em> can match specific words and translate them to <em>variables</em>.<br/>
          They can be used in <em>intents</em> with the syntax <code>@myvariable</code>
        </template>Entities</subtitle>
      <!-- list local entities -->
      <el-tree class="intent-list"
        ref="entitiesTree"
        :data="filteredEntities"
        node-key="entity"
        :default-expand-all="!!delayedFilter"
        :default-expanded-keys="expanded.entities"
        v-if="filteredEntities.length">
        <template v-slot="{ data: entity }">
          <div class="intent-list-item">
            <span v-if="entity.type==='node'"
              class="intent-list-main">
              <span class="intent-list-count"
                >&lt;{{ entity.children.length }}&gt;</span>
              <span class="intent-list-node intent-list-name">
                {{entity.shortName}}
              </span>
            </span>
            <span v-else class="intent-list-main"
              v-id="'entities-' + entity.entity">
              <span class="intent-list-count"
                >({{ entity.first.count || '-' }})</span>
              <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
                <i :class="entity.first.status==='deleted'?'el-icon-circle-close':entity.first.status==='created'?'el-icon-circle-plus-outline':entity.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
                <template slot="content">
                  {{entity.first.explanation}}
                </template>
              </el-tooltip>
              <!-- view version (or compare if not in view) -->
              <router-link
                :to="{name: 'entity', params: {skill: skill, entity: entity.entity}, query: entity.query}"
                class="intent-list-intent intent-list-name"
                :class="'intent-list-' + entity.first.source"
                >{{entity.shortName}}</router-link>
              <!-- compare version if different from view -->
              <router-link v-if="entity.second"
                :to="{name: 'entity', params: {skill: skill, entity: entity.entity}, query: entity.query}"
                class="intent-list-intent intent-list-name"
                :class="'intent-list-' + entity.second.source"
                >{{shortenName(entity.prefix, entity.second.name)}}</router-link>
            </span>
          </div>
        </template>
      </el-tree>
      <!-- new entity link -->
      <div class="intent-list">
        <div class="intent-list-item" style="margin-top: 0.5em"
          v-if="!view">
          <span class="intent-list-main">
            <span class="intent-list-count"
              >&nbsp;+</span>
            <router-link
              :to="{name: 'entity', params: {skill: skill, entity: 'new'}}"
              class="intent-list-intent intent-list-add"
              >New entity</router-link>
          </span>
        </div>
      </div>
    </div>
    <!-- synonyms -->
    <div v-if="!delayedFilter || filteredSynonyms.length">
      <subtitle id="synonyms">
        <template slot="tooltip">
          <em>Synonyms</em> help your intents to be more versatile.<br/>
          They can be used in <em>intents</em> with the syntax <code>#mysynonym</code>
        </template>Synonyms</subtitle>
      <!-- list local synonyms -->
      <el-tree class="intent-list"
        ref="synonymsTree"
        :data="filteredSynonyms"
        node-key="synonym"
        :default-expand-all="!!delayedFilter"
        :default-expanded-keys="expanded.synonyms"
        v-if="filteredSynonyms.length">
        <template v-slot="{ data: synonym }">
          <div class="intent-list-item">
            <span v-if="synonym.type==='node'"
              class="intent-list-main">
              <span class="intent-list-count"
                >&lt;{{ synonym.children.length }}&gt;</span>
              <span class="intent-list-node intent-list-name">
                {{synonym.shortName}}
              </span>
            </span>
            <span v-else class="intent-list-main"
              v-id="'synonyms-' + synonym.synonym">
              <span class="intent-list-count"
                >({{ synonym.first.count || '-' }})</span>
              <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
                <i :class="synonym.first.status==='deleted'?'el-icon-circle-close':synonym.first.status==='created'?'el-icon-circle-plus-outline':synonym.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
                <template slot="content">
                  {{synonym.first.explanation}}
                </template>
              </el-tooltip>
              <!-- view version (or compare if not in view) -->
              <router-link
                :to="{name: 'synonym', params: {skill: skill, synonym: synonym.synonym}, query: synonym.query}"
                class="intent-list-intent intent-list-name"
                :class="'intent-list-' + synonym.first.source"
                >{{synonym.shortName}}</router-link>
              <!-- compare version if different from view -->
              <router-link v-if="synonym.second"
                :to="{name: 'synonym', params: {skill: skill, synonym: synonym.synonym}, query: synonym.query}"
                class="intent-list-intent intent-list-name"
                :class="'intent-list-' + synonym.second.source"
                >{{shortenName(synonym.prefix, synonym.second.name)}}</router-link>
            </span>
          </div>
        </template>
      </el-tree>
      <!-- new synonym link -->
      <div class="intent-list">
        <div class="intent-list-item" style="margin-top: 0.5em"
          v-if="!view">
          <span class="intent-list-main">
            <span class="intent-list-count"
              >&nbsp;+</span>
            <router-link
              :to="{name: 'synonym', params: {skill: skill, synonym: 'new'}}"
              class="intent-list-intent intent-list-add"
              >New synonym</router-link>
          </span>
        </div>
      </div>
    </div>
    <!-- APIs -->
    <div v-if="!delayedFilter || filteredApis.length">
      <subtitle id="apis">
        <template slot="tooltip">
          <em>APIs</em> define the URLs and authentications methods to access to an API.
        </template>APIs</subtitle>
      <!-- list local APIs -->
      <el-tree class="intent-list"
        ref="apisTree"
        :data="filteredApis"
        node-key="api"
        :default-expand-all="!!delayedFilter"
        :default-expanded-keys="expanded.apis"
        v-if="filteredApis.length">
        <template v-slot="{ data: api }">
          <div class="intent-list-item">
            <span v-if="api.type==='node'"
              class="intent-list-main">
              <span class="intent-list-count"
                >&lt;{{ api.children.length }}&gt;</span>
              <span class="intent-list-node intent-list-name">
                {{api.shortName}}
              </span>
            </span>
            <span v-else class="intent-list-main"
              v-id="'apis-' + api.api">
              <span class="intent-list-count"
                >({{ api.first.count || '-' }})</span>
              <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
                <i :class="api.first.status==='deleted'?'el-icon-circle-close':api.first.status==='created'?'el-icon-circle-plus-outline':api.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
                <template slot="content">
                  {{api.first.explanation}}
                </template>
              </el-tooltip>
              <!-- view version (or compare if not in view) -->
              <router-link
                :to="{name: 'api', params: {skill: skill, api: api.api}, query: api.query}"
                class="intent-list-intent intent-list-name"
                :class="'intent-list-' + api.first.source"
                >{{api.shortName}}</router-link>
              <!-- compare version if different from view -->
              <router-link v-if="api.second"
                :to="{name: 'api', params: {skill: skill, api: api.api}, query: api.query}"
                class="intent-list-intent intent-list-name"
                :class="'intent-list-' + api.second.source"
                >{{shortenName(api.prefix, api.second.name)}}</router-link>
            </span>
          </div>
        </template>
      </el-tree>
      <!-- new API link -->
      <div class="intent-list">
        <div class="intent-list-item" style="margin-top: 0.5em"
          v-if="!view">
          <span class="intent-list-main">
            <span class="intent-list-count"
              >&nbsp;+</span>
            <router-link
              :to="{name: 'api', params: {skill: skill, api: 'new'}}"
              class="intent-list-intent intent-list-add"
              >New api</router-link>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import State from '../../stores/state.js'
import { comparedItems, buildTree, filterTree, TIMESTAMP, NAMES } from '../../utils/compare.js'
import ViewSelector from '../ViewSelector.vue'
import SkillEditor from './SkillEditor.vue'

export default {
  name: 'SkillPage',
  components: {
    ViewSelector,
    SkillEditor,
  },
  props: {
    skill: String,
    view: String,
    compare: String,
  },
  data () {
    return {
      state: State.state,
      viewObj: State.empty,
      compareObj: { data: null },
      editing: false,
      expanded: {
        intents: [],
        entities: [],
        synonyms: [],
        apis: [],
      },
      filter: '',
      delayedFilter: '',
    }
  },
  computed: {
    // shortcuts for some fields
    value () { return this.viewObj.data },
    other () { return this.compareObj.data },
    status () { return this.value.status || null },
    timestamp () { return this.value.timestamp || null },
    published () { return this.value.published || null },
    approved () { return this.value.approved || null },
    // list all intents, compared, as a tree
    listIntents () {
      // the field for comparing intents
      let newt = TIMESTAMP[this.$route.query.view || 'dev']
      let oldt = TIMESTAMP[this.$route.query.compare || 'dev']

      let intents = {} // all intent obj from view by intentid
      let contextsd = { '': {}, '#': {} } // {context: {intentid: obj}} dict
      let contextsl = [] // list of all contexts for sorting
      let followups = {} // {parentid: {intentid: obj}} dict for followup contexts
      // add all known contexts, as some could have no intents
      for (let context of this.value.contexts) {
        if (context.context &&
          context.context.substr(0, 'followup:'.length) === 'followup:') {
          continue
        }
        contextsd[context.context] = {}
        contextsl.push(context.context)
      }
      // get every view intents
      for (let intent of this.value.intents) {
        let context = intent.context || '', followup = null
        if (context.substr(0, 'followup:'.length) === 'followup:') {
          followup = context.substr('followup:'.length)
        }
        let status, explanation, special = intent.intent.length !== 32
        if (special) context = '#' // special context for special intents
        /* get status and explanation for the intent version */
        // there is a compare, status is about comparing
        // let's temporarily assume that the intent is only in view
        if (this.other) {
          status = 'created'
          explanation = 'intent only in ' + NAMES[newt]
        // ther is no compare, status is about dev
        // doesn't exist in development
        } else if (!intent.timestamp) {
          // not a special, so has been deleted
          if (!special) {
            status = 'deleted'
            explanation = 'intent deleted in development'
          // a special, but defined in another version
          } else if (intent.published || intent.approved) {
            status = 'edited'
            explanation = 'special intent with default value in development'
          // a special with just default value
          } else {
            status = null
            explanation = 'special intent with default value'
          }
        // only in development
        } else if (!intent.published && !intent.approved) {
          // not a psecial, created in development
          if (!special) {
            status = 'created'
            explanation = 'intent created in development'
          // a special, has default value in other versions
          } else {
            status = 'edited'
            explanation = 'special intent edited in development'
          }
        // development version is different form other view, edited
        } else if (intent.timestamp !== (newt === 'timestamp'
          ? intent.published || intent.approved : intent[newt])) {
          status = 'edited'
          explanation = (special ? 'special ' : '') + 'intent edited in development'
        // exactly the same everywhere
        } else {
          status = null
          explanation = (special ? 'special ' : '') + 'intent synchronized'
        }
        // build the object for display
        let obj = {
          intent: intent.intent,
          first: { // first name to display
            ...intent,
            followup,
            status,
            explanation,
            // for displaying diff
            source: this.other ? 'new' : 'same',
          },
          special, // if special
          // query arguments for the link (assuming not in compare)
          query: { view: this.$route.query.view },
          // all output contexts
          outputs: intent.outputs.map(output => {
            let followup = null
            if (output.context.substr(0, 'followup:'.length) === 'followup:') {
              followup = output.context.substr('followup:'.length)
            }
            return { ...output, followup, source: this.other ? 'new' : 'same' }
          }),
          // all folloa intents
          follow: intent.follow.map(follow => {
            return { ...follow, source: this.other ? 'new' : 'same' }
          }),
        }
        intents[intent.intent] = obj
        // followup context, add it in followups
        if (followup) {
          if (!(followup in followups)) followups[followup] = {}
          followups[followup][intent.intent] = obj
        // normal context, add it to contextsd
        } else contextsd[context][intent.intent] = obj
      }
      // add all known contexts from other, if not already added
      for (let context of (this.other || {}).contexts || []) {
        if (context.context in contextsd) continue
        if (context.context &&
          context.context.substr(0, 'followup:'.length) === 'followup:') {
          continue
        }
        contextsd[context.context] = {}
        contextsl.push(context.context)
      }

      // the view argument of objects that are only in compare
      let compare = this.$route.query.compare
      if (compare === 'dev') compare = undefined
      // get every compare intents
      for (let intent of (this.other || {}).intents || []) {
        let context = intent.context || '', followup = null
        if (intent.intent.length !== 32) context = '#'
        let obj = intents[intent.intent]
        // get back the object (if exists and same context)
        if (context.substr(0, 'followup:'.length) === 'followup:') {
          followup = context.substr('followup:'.length)
        }
        // obj exists, update it
        if (obj) {
          obj.query.compare = this.$route.query.compare
          // both versions are identical
          if (obj.first[newt] === obj.first[oldt]) {
            obj.first.status = null
            obj.first.explanation = (obj.special ? 'special ' : '') + 'intent synchronized'
            obj.first.source = 'same'
          // versions are different
          } else {
            obj.first.status = 'edited'
            obj.first.explanation = (obj.special ? 'special ' : '') + 'intent different in ' + NAMES[newt]
            // versions have different name, store the second one in second
            if (obj.first.name !== intent.name ||
              obj.first.context !== intent.context) {
              obj.second = {
                ...intent,
                followup,
                status: 'edited',
                explanation: (obj.special ? 'special ' : '') + 'intent different from ' + NAMES[newt],
                source: 'old',
              }
            // versions have the same name
            } else {
              obj.first.source = 'same'
            }
          }
          // get all output contexts
          let outputs = {}
          for (let output of obj.outputs) {
            outputs[output.context] = output
          }
          // compare output contexts of versions
          for (let output of intent.outputs) {
            let newo = outputs[output.context]
            if (newo && newo.exclusive === output.exclusive &&
              Math.sign(newo.ttl) === Math.sign(output.ttl)) {
              newo.source = 'same'
            } else {
              let followup = null
              if (output.context.substr(0, 'followup:'.length) === 'followup:') {
                followup = output.context.substr('followup:'.length)
              }
              obj.outputs.push({ ...output, followup, source: 'old' })
            }
          }
          // get all follow intents
          let follows = {}
          for (let follow of obj.follow) {
            follows[follow.skill + ':' + follow.intent] = follow
          }
          // compare follow intents of versions
          for (let follow of intent.follow) {
            let newf = follows[follow.skill + ':' + follow.intent]
            if (newf) {
              newf.source = 'same'
            } else {
              obj.follow.push({ ...follow, source: 'old' })
            }
          }
          continue
        }
        // only in compare, create the object to display it
        obj = {
          type: 'intent',
          intent: intent.intent,
          first: {
            ...intent,
            followup,
            status: 'deleted',
            explanation: 'intent absent in ' + NAMES[newt],
            source: 'old',
          },
          special: false,
          query: { view: compare },
          outputs: intent.outputs.map(output => {
            let followup = null
            if (output.context.substr(0, 'followup:'.length) === 'followup:') {
              followup = output.context.substr('followup:'.length)
            }
            return { ...output, followup, source: 'old' }
          }),
          follow: intent.follow.map(follow => {
            return { ...follow, source: 'old' }
          }),
        }
        // followup context, add it in followups
        if (followup) {
          if (!(followup in followups)) followups[followup] = {}
          followups[followup][intent.intent] = obj
        } else contextsd[context][intent.intent] = obj
        // normal context, add it to contextsd
      }
      // sort context, with special and global at first
      contextsl.sort()
      contextsl.unshift('')
      contextsl.unshift('#')
      // create the children array of each intent
      let children = {}
      for (let parent in followups) {
        let flu = followups[parent], intents = []
        for (let intentid in flu) {
          intents.push(flu[intentid])
        }
        children[parent] = intents
      }
      // create the intents tree of each context
      return contextsl.map(context => {
        let intents = []
        for (let intent in contextsd[context]) {
          intents.push(contextsd[context][intent])
        }
        return {
          type: 'context',
          context: context,
          intents: buildTree(intents, true, children, 'intent'),
        }
      })
    },
    filteredIntents () {
      if (!this.delayedFilter) return this.listIntents
      else {
        return this.listIntents.map(context => {
          return {
            ...context,
            intents: filterTree(this.delayedFilter, context.intents, true),
          }
        }).filter(context => context.intents.length)
      }
    },
    // all intents by intentid
    allIntents () {
      let out = {}
      // from view
      for (let intent of this.value.intents) {
        out[intent.intent] = {
          ...intent,
          source: 'new',
          query: { view: this.$route.query.view },
        }
      }
      // add it compare
      let compare = this.$route.query.compare
      if (compare === 'dev') compare = undefined
      for (let intent of (this.other || {}).intents || []) {
        let obj = out[intent.intent]
        // already in, update source and query
        if (obj) {
          obj.source = 'same'
          obj.query.compare = this.$route.query.compare
          continue
        }
        // not in view
        out[intent.intent] = {
          ...intent,
          source: 'old',
          query: { view: compare },
        }
      }
      return out
    },
    // all public intents by skillid and intentid
    allPublic () {
      let out = {}
      for (let skill of this.value.public) {
        out[skill.skill] = {}
        for (let intent of skill.intents) {
          out[skill.skill][intent.intent] = {
            skill: skill.name,
            intent: intent.name,
            variables: intent.variables,
          }
        }
      }
      return out
    },
    // all local entities as a compared tree
    localEntities () {
      let entities = comparedItems('entity',
        this.value.entities.filter(x => x.local),
        this.other && this.other.entities.filter(x => x.local),
        this.$route.query)

      let data = []
      for (let entity in entities) {
        data.push(entities[entity])
      }
      return buildTree(data, true)
    },
    filteredEntities () {
      if (!this.delayedFilter) return this.localEntities
      else return filterTree(this.delayedFilter, this.localEntities, true)
    },
    // all local synonyms as a compared tree
    localSynonyms () {
      let synonyms = comparedItems('synonym',
        this.value.synonyms.filter(x => x.local),
        this.other && this.other.synonyms.filter(x => x.local),
        this.$route.query)

      let data = []
      for (let synonym in synonyms) {
        data.push(synonyms[synonym])
      }
      return buildTree(data, true)
    },
    filteredSynonyms () {
      if (!this.delayedFilter) return this.localSynonyms
      else return filterTree(this.delayedFilter, this.localSynonyms, true)
    },
    // all local Apis as a compared tree
    localApis () {
      let apis = comparedItems('api',
        this.value.apis.filter(x => x.local),
        this.other && this.other.apis.filter(x => x.local),
        this.$route.query)

      let data = []
      for (let api in apis) {
        data.push(apis[api])
      }
      return buildTree(data, true)
    },
    filteredApis () {
      if (!this.delayedFilter) return this.localApis
      else return filterTree(this.delayedFilter, this.localApis, true)
    },
  },
  inject: ['onerror'],
  // loads everything, look for URL hash
  created () {
    this.expandFromHash()
    Promise.all([
      this.loadView(),
      this.loadCompare(),
    ]).finally(() => {
      this.$anchorsLoaded()
    })
  },
  watch: {
    // user connected, reload everything
    'state.user' () {
      this.loadView()
      this.loadCompare()
    },
    // skill has changed, reload everything
    skill () {
      this.loadView()
      this.loadCompare()
    },
    // reload view
    view () { this.loadView() },
    // reload compare
    compare () { this.loadCompare() },
    // expand the item targetd by the hash
    '$route.hash' () { this.expandFromHash() },
    // update fitler with a delay
    filter (f) {
      if (!f) this.delayedFilter = f
      else {
        setInterval(() => {
          if (this.filter === f) this.delayedFilter = f
        }, 200)
      }
    },
    delayedFilter (f) {
      ;(this.$refs.intentsTrees || []).forEach(tree => {
        tree.store.defaultExpandAll = !!f
      })
      ;(this.$refs.entitiesTree || {}).defaultExpandAll = !!f
      ;(this.$refs.synonymsTree || {}).defaultExpandAll = !!f
      ;(this.$refs.apisTree || {}).defaultExpandAll = !!f
    },
  },
  methods: {
    // load view skill
    loadView () {
      let skill = this.skill, view = this.view
      return State.getSkill(skill, view, true)
        .then(data => {
          if (skill !== this.skill || view !== this.view) return
          this.viewObj = data
        }).catch(this.onerror)
    },
    // load compare skill
    loadCompare () {
      let skill = this.skill, view = this.compare
      if (!view) {
        this.compareObj = { data: null }
        return Promise.resolve()
      } else {
        return State.getSkill(skill, view, true)
          .then(data => {
            if (skill !== this.skill || view !== this.compare) return
            this.compareObj = data
          }).catch(this.onerror)
      }
    },
    // remove skill
    remove () {
      return State.removeSkill(this.skill).then(() => {
        this.$router.push({ name: 'home', query: this.$route.query })
      }).catch(() => {})
    },
    // open skill editor
    edit () {
      this.editing = true
    },
    // save skill
    save (value) {
      State.saveSkill(this.skill, value).then(() => {
        this.$message({
          message: 'Skill saved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // untest skill
    untest () {
      State.untestSkill(this.skill).then(() => {
        this.$message({
          message: 'Test cleared',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // test skill
    test () {
      State.testSkill(this.skill).then(() => {
        this.$message({
          message: 'Skill in test',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // unpublish skill
    unpublish () {
      State.unpublishSkill(this.skill, this.published).then(() => {
        this.$message({
          message: 'Skill unpublished',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // unprod skill
    unprod () {
      State.publishSkill(this.skill, false).then(() => {
        this.$message({
          message: 'Skill unproded',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // publish skill
    publish () {
      State.publishSkill(this.skill, this.timestamp).then(() => {
        this.$message({
          message: 'Skill published',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // disapprove skill
    disapprove () {
      State.disapproveSkill(this.skill, this.approved).then(() => {
        this.$message({
          message: 'Skill disapproved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // reject skll
    reject () {
      State.rejectSkill(this.skill, this.published).then(() => {
        this.$message({
          message: 'Skill rejected',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // approve skill
    approve () {
      State.approveSkill(this.skill, this.published).then(data => {
        this.$message({
          message: data.published ? 'Skill approved' : 'Deletion approved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // restore skill
    restore (version) {
      let timestamp = version === 'pub' ? this.published : this.approved
      let name = version === 'pub' ? 'Published' : 'Approved'
      console.log('restore', version, timestamp, name)
      State['restore' + name + 'Skill'](this.skill, timestamp).then(() => {
        this.$message({
          message: name + ' skill restored',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // change view or compare
    changeView (view, compare) {
      this.$router.push({ query: { view: view, compare: compare } })
    },
    // expand tree items if they match the URL hash
    expandFromHash () {
      let hash = this.$route.hash
      hash = hash && hash.substr(1).split('-')
      if (!hash || hash.length !== 2 || hash[1].length !== 32 ||
        !this.expanded[hash[0]]) return
      this.expanded[hash[0]] = [hash[1]]
    },
    shortenName (prefix, name) {
      if (prefix && name.substr(0, prefix.length) === prefix) {
        return '…' + name.substr(prefix.length)
      } else return name
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";
  @import "../../variables.scss";

  #skill-page-filter {
    position: sticky;
    top: 0;
    z-index: $overlay-z-index;
    input:not(:hover):not(:focus) {
      border-top-color: transparent;
      border-right-color: transparent;
      border-left-color: transparent;
    }
  }
  .intent-list-intent {
    color: $--color-text-regular;
    /*font-weight: bold;*/
    text-decoration: none;
    margin-right: 5px;
    white-space: nowrap;
  }
  .intent-list-output {
    white-space: nowrap;
  }
  .intent-list-intent:hover {
    text-decoration: underline;
  }
  .intent-list-output-followup {
    color: $--color-text-placeholder;
    /*font-weight: bold;*/
    text-decoration: none;
    &:hover {
      color: $--color-text-regular;
    }
  }
  .intent-list-add {
    font-style: italic;
  }
  .intent-list-new {
    background-color: $--color-primary-lighter;
  }
  .intent-list-old {
    background-color: $--color-warning-lighter;
  }
  .intent-list-main {
    display: inline-block;
    min-width: 200px;
    margin-left: -200px;

    .el-icon-circle-check {
      color: $--color-success;
      margin-right: 5px;
    }
    .el-icon-circle-plus-outline {
      color: $--color-primary;
      margin-right: 5px;
    }
    .el-icon-warning-outline {
      color: $--color-primary;
      margin-right: 5px;
    }
    .el-icon-circle-close {
      color: $--color-warning;
      margin-right: 5px;
    }
  }
  .intent-list-infos {
    .el-icon-error {
      color: $--color-danger;
    }
  }
  .intent-list-count {
    font-family: monospace;
    display: inline-block;
    width: 32px;
  }
  .intent-list-context-followup {
    color: $--color-text-placeholder;
    text-decoration: none;
    &:hover {
      color: $--color-text-regular;
    }
  }
  .intent-list-add-followup {
    line-height: 18px;
    color: $--color-text-placeholder;
    text-decoration: none;
    &:hover {
      color: $--color-text-regular;
    }
  }
  .intent-list, .el-tree.intent-list {
    color: $--color-text-placeholder;
    padding-left: 40px;
  }
  .intent-list .el-tree-node {
    white-space: normal;
  }
  .intent-list .el-tree-node__content {
    height: auto;
    cursor: default;
    align-items: flex-start;
    > .el-tree-node__expand-icon {
      padding: 0 6px;
      margin-left: -24px;
      line-height: 18px;
    }
  }
  .intent-list .el-tree-node__children {
    margin-left: 16px;
  }
  .intent-list .intent-list-item {
    margin-left: 200px;
    &:hover, &:hover > * {
      background-color: $--background-color-base;
    }
  }
  .intent-list-output-delete {
    text-decoration: line-through;
  }
  .intent-list-output-exclusive {
    text-decoration: underline;
  }
  .intent-list-output-extend {
    font-style: italic;
  }
</style>
